import React, { Component } from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { PositionType } from '@atlaskit/tooltip';
import FavoriteButtonStateless from '@atlassian/jira-favourite-button-stateless/src/view/view.tsx';
import { VALID_ITEM_TYPES } from '@atlassian/jira-favourite-change-provider/src/model/constants.tsx';
import type {
	ItemType,
	FavoriteChangeContextType,
} from '@atlassian/jira-favourite-change-provider/src/model/types.tsx';
import { getFavorite } from '../rest/get-favorite.tsx';

export type OwnProps = {
	isFavoriteInitial?: boolean | undefined;
	favoriteItemName?: string | undefined;
	// whether to show small star icon
	isSmall?: boolean;
	isShownInList?: boolean;
	baseUrl: string;
	itemId: string;
	itemType: ItemType;
	hideTooltip?: boolean;
	tooltipPosition?: PositionType;
	onClick?: () => void;
};

export type Props = OwnProps & {
	favoriteChangeContext: FavoriteChangeContextType;
};

type State = {
	isFavorite: boolean | undefined;
};

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const isProduction = window.BUILD_KEY !== 'JF-TEST';

// eslint-disable-next-line jira/react/no-class-components
export class FavoriteButton extends Component<Props, State> {
	static defaultProps = {
		isFavoriteInitial: undefined,
		favoriteItemName: undefined,
		isSmall: false,
		hideTooltip: false,
		onClick: noop,
	};

	constructor(props: Props) {
		super(props);
		this.state = {
			isFavorite: props.isFavoriteInitial,
		};
	}

	componentDidMount() {
		const { baseUrl, itemId, itemType } = this.props;
		if (
			typeof baseUrl === 'undefined' ||
			typeof itemId === 'undefined' ||
			typeof itemType === 'undefined'
		) {
			return;
		}

		// Adding an Error here for the next dev to implement this button.
		// This error won't show in prod, but will show when running a fragment.
		if (!isProduction && !VALID_ITEM_TYPES.includes(itemType)) {
			throw new Error(
				`FavoriteButton: Invalid value for prop 'type'.\n You passed: "${itemType}"\n It must be one of [${JSON.stringify(
					VALID_ITEM_TYPES,
				)}]\n\n`,
			);
		}

		if (typeof this.props.isFavoriteInitial !== 'undefined') {
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
			this.props.favoriteChangeContext.items[itemType][itemId] = {
				id: itemId,
				type: itemType,
				value: this.props.isFavoriteInitial,
				pending: false,
			};
		}

		if (this.getIsFavorite() === undefined) {
			getFavorite(baseUrl, {
				id: itemId,
				type: itemType,
			}).then((isFavorite) => {
				this.setState({
					isFavorite,
				});
			});
		}
	}

	onClick = (analyticsEvent: UIAnalyticsEvent) => {
		const { favoriteChangeContext, itemId, itemType, onClick } = this.props;

		const { changeFavorite } = favoriteChangeContext;
		if (changeFavorite) {
			changeFavorite(
				{
					id: itemId,
					type: itemType,
					value: !this.getIsFavorite(),
				},
				analyticsEvent,
			);
		}

		if (typeof onClick === 'function') onClick();
	};

	getIsFavorite() {
		const { favoriteChangeContext, itemId, itemType } = this.props;

		let itemChanged;
		if (
			typeof favoriteChangeContext.items[itemType] !== 'undefined' &&
			// @ts-expect-error - TS2532 - TS doesn't refine types with index access in this version.
			typeof favoriteChangeContext.items[itemType][itemId] !== 'undefined'
		) {
			// @ts-expect-error - TS2532 - TS doesn't refine types with index access in this version.
			itemChanged = favoriteChangeContext.items[itemType][itemId];
		}

		return itemChanged ? itemChanged.value : this.state.isFavorite;
	}

	getIsPending() {
		const { favoriteChangeContext, itemId, itemType } = this.props;
		let itemChanged;

		if (
			typeof favoriteChangeContext.items[itemType] !== 'undefined' &&
			// @ts-expect-error - TS2532 - TS doesn't refine types with index access in this version.
			typeof favoriteChangeContext.items[itemType][itemId] !== 'undefined'
		) {
			// @ts-expect-error - TS2532 - TS doesn't refine types with index access in this version.
			itemChanged = favoriteChangeContext.items[itemType][itemId];
		}

		return itemChanged ? itemChanged.pending : false;
	}

	render() {
		return (
			<div data-testid="favourite-button.favorite-button">
				<FavoriteButtonStateless
					isFavorite={this.getIsFavorite()}
					favoriteItemName={this.props.favoriteItemName}
					pending={this.getIsPending()}
					hideTooltip={this.props.hideTooltip}
					tooltipPosition={this.props.tooltipPosition}
					onClick={this.onClick}
					isShownInList={this.props.isShownInList}
					isSmall={this.props.isSmall}
				/>
			</div>
		);
	}
}
